'use client'

import { useEffect, useState } from 'react'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { useSearchParams } from 'next/navigation'
import { UAParser } from 'ua-parser-js'

import { cn } from '@/lib/classnames'

export type GuideDevice = 'ios' | 'samsung'

export type GuideItem = {
  label: string
  href?: string
  step?: number
  hint?: string
  comingSoon?: boolean
}

type DeviceGuide = {
  name: string
  items: GuideItem[]
  detailedHref: string
  detailedLabel: string
}

type GuideLabels = {
  comingSoon: string
  detailedTitle: string
  detailedDescription: string
}

const devicesOrder: GuideDevice[] = ['ios', 'samsung']

const itemClassName = 'flex w-full items-center gap-3 rounded-xl border border-orange-200 bg-orange-50 px-3.5 py-3 text-left text-blue-950 hover:border-orange-400 hover:bg-orange-100'

function detectGuideDevice(userAgent: string): GuideDevice {
  const { os, device } = new UAParser(userAgent).getResult()
  const osName = os.name || ''
  if (osName === 'Android' || device.vendor === 'Samsung') return 'samsung'
  return 'ios'
}

function tabIndex(value: string | null) {
  if (value === 'samsung') return 1
  if (value === 'ios') return 0
  return null
}

export function InstallationGuideTabs({ devices, labels }: { devices: Record<GuideDevice, DeviceGuide>; labels: GuideLabels }) {
  const searchParams = useSearchParams()
  const preset = tabIndex(searchParams.get('tab'))
  const [selectedIndex, setSelectedIndex] = useState(preset ?? 0)

  useEffect(() => {
    if (preset !== null) {
      setSelectedIndex(preset)
      return
    }
    setSelectedIndex(detectGuideDevice(navigator.userAgent) === 'samsung' ? 1 : 0)
  }, [preset])

  return (
    <TabGroup selectedIndex={selectedIndex} onChange={setSelectedIndex}>
      <TabList className='grid grid-cols-2 gap-1 rounded-2xl bg-gray-200 p-1'>
        {devicesOrder.map(key => (
          <Tab
            key={key}
            className={cn(
              'rounded-xl px-4 py-3 text-base font-bold outline-none transition-colors',
              'text-gray-600 hover:text-blue-950',
              'data-[selected]:bg-blue-950 data-[selected]:text-white data-[selected]:shadow-sm',
              'focus-visible:ring-2 focus-visible:ring-blue-950 focus-visible:ring-offset-2',
            )}
          >
            {devices[key].name}
          </Tab>
        ))}
      </TabList>
      <TabPanels className='mt-4'>
        {devicesOrder.map(key => {
          const device = devices[key]
          return (
            <TabPanel key={key} className='rounded-2xl border border-gray-200 bg-white p-5 shadow-sm outline-none'>
              <ol className='flex flex-col gap-2'>
                {device.items.map(item => (
                  <li key={item.label}>
                    <GuideItemRow item={item} comingSoonLabel={labels.comingSoon} />
                  </li>
                ))}
              </ol>
              <div className='mt-6 border-t border-gray-100 pt-5'>
                <h2 className='text-base font-bold text-blue-950'>{labels.detailedTitle}</h2>
                <p className='mt-1 text-sm text-gray-500'>{labels.detailedDescription}</p>
                <div className='mt-4'>
                  <GuideItemRow item={{ label: device.detailedLabel, href: device.detailedHref }} comingSoonLabel={labels.comingSoon} />
                </div>
              </div>
            </TabPanel>
          )
        })}
      </TabPanels>
    </TabGroup>
  )
}

function GuideItemRow({ item, comingSoonLabel }: { item: GuideItem; comingSoonLabel: string }) {
  const showIndex = item.step != null || item.comingSoon
  const content = (
    <>
      {showIndex ? (
        <span
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-bold',
            item.comingSoon ? 'border-gray-200 bg-white text-gray-400' : 'border-orange-200 bg-white text-blue-950',
          )}
        >
          {item.step ?? ''}
        </span>
      ) : null}
      <span className='min-w-0 flex-1'>
        <span className='block font-semibold leading-snug'>{item.label}</span>
        {item.hint ? <span className={cn('mt-0.5 block text-[13px] font-semibold', item.comingSoon ? 'text-gray-400' : 'text-gray-800')}>{item.hint}</span> : null}
      </span>
      {item.comingSoon ? (
        <span className='shrink-0 rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-gray-500'>{comingSoonLabel}</span>
      ) : (
        <ArrowTopRightOnSquareIcon className='h-5 w-5 shrink-0 text-orange-400' />
      )}
    </>
  )

  if (item.comingSoon || !item.href) {
    return <div className='flex w-full items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3 text-gray-400'>{content}</div>
  }

  return (
    <a href={item.href} target='_blank' rel='noreferrer' className={itemClassName}>
      {content}
    </a>
  )
}
